import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Paper } from "@mui/material";
import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import { useIntl } from "react-intl";

import { ILegend, ITemplateData } from "@framework/types";

import { useAlarmPaletteResolver } from "../../../../../../hooks/use-alarm-color";
import { ALARMS } from "../../../../../../utils";
import { getAnalyticsBarFillAndStroke, getSize } from "./utils";

export type TFileMap = Map<string, { name: string; input: Blob }>;

export interface IBarDiagramProps {
  data: ITemplateData[];
  size?: string;
  flipped?: boolean;
  yLabel?: string;
  chartsRef?: MutableRefObject<TFileMap>;
  header?: string;
}

interface IGroupInfo {
  name: string;
  order: number;
}

interface IGroupBucket {
  order: number;
  values: ITemplateData[];
}

interface ILayerBucket {
  order: number;
  groups: Record<string, IGroupBucket>;
}

interface IChartBucket {
  order: number;
  layers: Record<string, ILayerBucket>;
  legends: ILegend[];
}

interface IBarChartPoint {
  x: string;
  y: number | null;
  fillColor: string;
  strokeColor: string;
}

interface IBarChartSeries {
  name: string;
  data: IBarChartPoint[];
  color: string;
}

interface IChartParams {
  key: string;
  title: string;
  options: ApexOptions;
  series: IBarChartSeries[];
}

interface IApexChartInstance {
  dataURI: () => Promise<{ imgURI: string }>;
}

interface IApexChartComponent {
  chart?: IApexChartInstance;
}

const CHART_HEIGHT = 400;
const PAPER_HEIGHT = 450;
const AXIS_COLOR = "#1f1f1f";
const GRID_COLOR = "#cfcfcf";
const CHART_FONT_FAMILY = "Arial";
const DATA_LABEL_FALLBACK_COLOR = "#333333";
const DATA_LABEL_OFFSET_Y = -26;
const COLUMN_SIDE_GAP_RATIO = 0.75;

const isWhite = (color?: string | null): boolean => {
  const normalized = color?.replace(/\s/g, "").toLowerCase();

  return ["#ffffff", "#fff", "white", "rgb(255,255,255)"].includes(normalized ?? "");
};

const isDataLabelColor = (color?: string | null): color is string => {
  return !!color && color !== "none" && !isWhite(color);
};

const resolveDataLabelColor = (rectFill?: string | null, textFill?: string | null): string => {
  if (isDataLabelColor(rectFill)) {
    return rectFill;
  }

  if (isDataLabelColor(textFill)) {
    return textFill;
  }

  return DATA_LABEL_FALLBACK_COLOR;
};

const getDataPointColor = ({ seriesIndex, dataPointIndex, w }: any): string => {
  const series = w?.config?.series?.[seriesIndex];
  const point = series?.data?.[dataPointIndex];
  const color = point?.fillColor || series?.color;

  return typeof color === "string" && color !== "none" ? color : DATA_LABEL_FALLBACK_COLOR;
};

const getSeriesColor = (series: IBarChartSeries): string => {
  return series.color !== "none" ? series.color : DATA_LABEL_FALLBACK_COLOR;
};

const getApexSeriesCount = (series: IBarChartSeries[]): number => {
  return Math.max(1, series.filter(item => item.data.length > 0).length);
};

const getColumnWidth = (series: IBarChartSeries[]): string => {
  const seriesCount = getApexSeriesCount(series);
  const columnWidth = seriesCount / (seriesCount + COLUMN_SIDE_GAP_RATIO * 2);

  return `${Math.round(columnWidth * 100)}%`;
};

const colorizeDataLabels = (chartContext: { el?: Element }): void => {
  const root = chartContext.el;

  if (!root) {
    return;
  }

  root.querySelectorAll<SVGGElement>(".apexcharts-data-labels").forEach(labelGroup => {
    const rect = labelGroup.querySelector<SVGRectElement>("rect");
    const text = labelGroup.querySelector<SVGTextElement>(".apexcharts-datalabel");

    if (!text) {
      return;
    }

    const color = resolveDataLabelColor(rect?.getAttribute("fill"), text.getAttribute("fill"));

    if (rect) {
      rect.setAttribute("fill", "#ffffff");
      rect.setAttribute("opacity", "1");
      rect.setAttribute("stroke", color);
      rect.setAttribute("stroke-width", "1");
    }

    text.setAttribute("fill", color);
  });
};

const toText = (value: string | number | undefined): string => {
  return value === undefined ? "" : String(value);
};

const getFirstText = (...values: Array<string | number | undefined>): string => {
  const value = values.find(item => item !== undefined && item !== "");

  return toText(value);
};

const getGroupName = (item: ITemplateData): string => {
  return getFirstText(item.SubName1, item.SubNum1, item.ValueName);
};

const getLayerName = (item: ITemplateData): string => {
  return getFirstText(item.LayerName, item.LayerNum);
};

const getGroupOrder = (item: ITemplateData): number => {
  return Number(item.SubNum1 || item.ValueNum || 0);
};

const getDiagramName = (item: ITemplateData): string => {
  return getFirstText(item.DiagramName, item.DiagramNum);
};

const upsertGroupInfo = (groups: IGroupInfo[], name: string, order: number): void => {
  const foundGroup = groups.find(group => group.name === name && group.order === order);

  if (!foundGroup) {
    groups.push({ name, order });
  }
};

const resolveSeriesName = (value: ITemplateData, groupName: string, legends: ILegend[]): string => {
  const legend = legends.find(item => value[item.LegendKey] === item.LegendValue);
  const name = legend
    ? legend.LegendName
    : value.ValueName || (value.AlarmStatus ? ALARMS[value.AlarmStatus] : groupName) || value.ValueNum;

  return String(name);
};

const findOrCreateSeries = (series: IBarChartSeries[], name: string, color: string): IBarChartSeries => {
  let foundSeries = series.find(item => item.name === name);

  if (!foundSeries) {
    foundSeries = {
      name,
      data: [],
      color,
    };
    series.push(foundSeries);
  } else if (foundSeries.color === "none" && color !== "none") {
    foundSeries.color = color;
  }

  return foundSeries;
};

const sortByOrder = <T extends { order: number }>(a: T, b: T): number => a.order - b.order;

const buildChartBuckets = (data: ITemplateData[]): { buckets: Record<string, IChartBucket>; groups: IGroupInfo[] } => {
  const groups: IGroupInfo[] = [];
  const buckets = data.reduce<Record<string, IChartBucket>>((acc, item) => {
    const diagramName = getDiagramName(item);
    const diagramOrder = Number(item.DiagramNum || 0);
    const layerName = getLayerName(item);
    const layerOrder = item.LayerNum || 0;
    const groupName = getGroupName(item);
    const groupOrder = getGroupOrder(item);

    if (!acc[diagramName]) {
      acc[diagramName] = {
        order: diagramOrder,
        layers: {},
        legends: [],
      };
    }

    if (item.Legend && acc[diagramName].legends.length === 0) {
      acc[diagramName].legends = JSON.parse(item.Legend) as ILegend[];
    }

    upsertGroupInfo(groups, groupName, groupOrder);

    if (!acc[diagramName].layers[layerName]) {
      acc[diagramName].layers[layerName] = {
        order: layerOrder,
        groups: {},
      };
    }

    if (!acc[diagramName].layers[layerName].groups[groupName]) {
      acc[diagramName].layers[layerName].groups[groupName] = {
        order: groupOrder,
        values: [],
      };
    }

    acc[diagramName].layers[layerName].groups[groupName].values.push(item);
    return acc;
  }, {});

  return { buckets, groups };
};

const addEmptyGroupPoint = (series: IBarChartSeries[], groupName: string, layerName: string): void => {
  const groupSeries = findOrCreateSeries(series, groupName, "none");

  groupSeries.data.push({
    x: layerName,
    y: null,
    fillColor: "none",
    strokeColor: "#888",
  });
};

const addValuePoint = (
  series: IBarChartSeries[],
  value: ITemplateData,
  layerName: string,
  groupName: string,
  legends: ILegend[],
  resolveAlarmColor: (status: number) => string,
): void => {
  const name = resolveSeriesName(value, groupName, legends);
  const { fill, stroke } = getAnalyticsBarFillAndStroke(value, legends, resolveAlarmColor);
  const valueSeries = findOrCreateSeries(series, name, fill);

  valueSeries.data.push({
    x: layerName,
    y: value.Value || 0,
    fillColor: fill,
    strokeColor: stroke,
  });
};

const buildSeries = (
  chart: IChartBucket,
  globalGroups: IGroupInfo[],
  resolveAlarmColor: (status: number) => string,
): IBarChartSeries[] => {
  const series: IBarChartSeries[] = [];
  const layers = Object.entries(chart.layers)
    .map(([name, layer]) => ({ name, ...layer }))
    .sort(sortByOrder);

  layers.forEach(layer => {
    const groups = globalGroups
      .map(group => ({
        ...group,
        values: layer.groups[group.name]?.values ?? [],
      }))
      .sort(sortByOrder);

    groups.forEach(group => {
      if (!group.values.length) {
        addEmptyGroupPoint(series, group.name, layer.name);
        return;
      }

      group.values
        .slice()
        .sort((a, b) => (~~a.ValueNum > ~~b.ValueNum ? 1 : -1))
        .forEach(value => addValuePoint(series, value, layer.name, group.name, chart.legends, resolveAlarmColor));
    });
  });

  return series;
};

const createChartOptions = ({
  categories,
  flipped,
  noDataText,
  series,
  title,
  yLabel,
}: {
  categories: string[];
  flipped: boolean;
  noDataText: string;
  series: IBarChartSeries[];
  title: string;
  yLabel: string;
}): ApexOptions => ({
  chart: {
    animations: {
      enabled: false,
    },
    events: {
      mounted: colorizeDataLabels,
      updated: colorizeDataLabels,
    },
    parentHeightOffset: 0,
    redrawOnParentResize: true,
    redrawOnWindowResize: true,
    type: "bar",
    height: CHART_HEIGHT,
    toolbar: {
      show: false,
    },
  },
  colors: series.map(getSeriesColor),
  title: {
    text: title,
    align: "center",
    margin: 12,
    style: {
      color: AXIS_COLOR,
      fontFamily: CHART_FONT_FAMILY,
      fontSize: "14px",
      fontWeight: 400,
    },
  },
  legend: {
    show: true,
    position: "top",
    offsetY: -6,
    fontFamily: CHART_FONT_FAMILY,
    fontSize: "12px",
    labels: {
      colors: AXIS_COLOR,
    },
  },
  dataLabels: {
    offsetY: DATA_LABEL_OFFSET_Y,
    style: {
      colors: series.map(() => getDataPointColor),
      fontFamily: CHART_FONT_FAMILY,
      fontSize: "11px",
      fontWeight: 500,
    },
    background: {
      enabled: true,
      foreColor: "#ffffff",
      borderColor: DATA_LABEL_FALLBACK_COLOR,
      borderRadius: 0,
      borderWidth: 1,
      padding: 2,
      opacity: 1,
    },
  },
  grid: {
    show: true,
    borderColor: GRID_COLOR,
    strokeDashArray: 0,
    xaxis: {
      lines: {
        show: true,
      },
    },
    yaxis: {
      lines: {
        show: true,
      },
    },
    padding: {
      top: 8,
      left: 8,
      right: 16,
    },
  },
  stroke: {
    show: true,
    width: 1,
  },
  noData: {
    text: noDataText,
    style: {
      color: AXIS_COLOR,
      fontFamily: CHART_FONT_FAMILY,
    },
  },
  plotOptions: {
    bar: {
      horizontal: flipped,
      columnWidth: getColumnWidth(series),
      dataLabels: {
        position: "top",
        orientation: "horizontal",
        hideOverflowingLabels: true,
      },
    },
  },
  xaxis: {
    categories,
    axisBorder: {
      show: true,
      color: GRID_COLOR,
    },
    axisTicks: {
      show: true,
      color: GRID_COLOR,
    },
    labels: {
      style: {
        colors: AXIS_COLOR,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: "12px",
        fontWeight: 500,
      },
    },
  },
  yaxis: {
    title: {
      text: yLabel,
      style: {
        color: AXIS_COLOR,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: "12px",
        fontWeight: 400,
      },
    },
    labels: {
      style: {
        colors: AXIS_COLOR,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: "12px",
        fontWeight: 500,
      },
    },
    axisBorder: {
      show: true,
      color: GRID_COLOR,
    },
    axisTicks: {
      show: true,
      color: GRID_COLOR,
    },
  },
});

const buildCharts = ({
  data,
  flipped,
  noDataText,
  resolveAlarmColor,
  yLabel,
}: {
  data: ITemplateData[];
  flipped: boolean;
  noDataText: string;
  resolveAlarmColor: (status: number) => string;
  yLabel: string;
}): IChartParams[] => {
  const { buckets, groups } = buildChartBuckets(data);

  return Object.entries(buckets)
    .sort(([, a], [, b]) => sortByOrder(a, b))
    .map(([title, chart]) => {
      const layers = Object.entries(chart.layers)
        .map(([name, layer]) => ({ name, ...layer }))
        .sort(sortByOrder);
      const categories = layers.map(layer => layer.name);
      const series = buildSeries(chart, groups, resolveAlarmColor);

      return {
        key: `${chart.order}-${title}`,
        title,
        options: createChartOptions({
          categories,
          flipped,
          noDataText,
          series,
          title,
          yLabel,
        }),
        series,
      };
    });
};

export const BarDiagram: FC<IBarDiagramProps> = props => {
  const { data, flipped = false, size, yLabel = "", chartsRef, header } = props;

  const chartRefs = useRef<Array<IApexChartComponent | null>>([]);
  const exportedChartNamesRef = useRef<string[]>([]);

  const { formatMessage } = useIntl();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const chartMinWidth = getSize(size);
  const noDataText = formatMessage({ id: "components.charts.noData" });
  const charts = useMemo(
    () =>
      buildCharts({
        data,
        flipped,
        noDataText,
        resolveAlarmColor,
        yLabel,
      }),
    [data, flipped, noDataText, resolveAlarmColor, yLabel],
  );

  const removeExportedCharts = useCallback((): void => {
    exportedChartNamesRef.current.forEach(name => {
      chartsRef?.current?.delete(name);
    });
    exportedChartNamesRef.current = [];
  }, [chartsRef]);

  const exportChartsToZip = useCallback(async (): Promise<void> => {
    if (!chartsRef?.current) {
      return;
    }

    removeExportedCharts();

    const exportedNames = await Promise.all(
      charts.map(async (chart, index) => {
        const chartInstance = chartRefs.current[index]?.chart;

        if (!chartInstance) {
          return null;
        }

        const { imgURI } = await chartInstance.dataURI();
        const response = await fetch(imgURI);
        const blob = await response.blob();
        const name = `analytics-chart${header ? `-${header}` : ""}-${index + 1}-${chart.title}.png`;

        chartsRef.current.set(name, { name, input: blob });
        return name;
      }),
    );

    exportedChartNamesRef.current = exportedNames.filter((name): name is string => !!name);
  }, [charts, chartsRef, header, removeExportedCharts]);

  useEffect(() => {
    void exportChartsToZip();
    return removeExportedCharts;
  }, [exportChartsToZip, removeExportedCharts]);

  return (
    <>
      {charts.map((chart, index) => (
        <Paper
          elevation={1}
          key={chart.key}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "stretch",
            width: "100%",
            maxWidth: "100%",
            height: PAPER_HEIGHT,
            overflowX: "auto",
          }}
        >
          <Box sx={{ width: "100%", minWidth: chartMinWidth }}>
            <Chart
              ref={el => {
                chartRefs.current[index] = el as IApexChartComponent | null;
              }}
              options={chart.options}
              series={chart.series}
              type="bar"
              height={CHART_HEIGHT}
              width="100%"
            />
          </Box>
        </Paper>
      ))}
    </>
  );
};
